import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FileSpreadsheet, Search, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { qualityNcrAPI } from '@/lib/api';
import { StatusBadge, saveBlob, errMsg, fmtDate } from './ncr/NcrShared';
import { LegacyImportButton } from './ncr/NcrExtras';
import { useAuth } from '@/contexts/AuthContext';

const STATUSES = ['', 'OPEN', 'RESOLVED', 'CLOSED'];

export default function InternalNCRPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await qualityNcrAPI.listInternal({ status: status || undefined, q: q || undefined })).data); }
    catch { toast.error('Failed to load register'); } finally { setLoading(false); }
  }, [status, q]);
  useEffect(() => { load(); }, [load]);
  const exportXlsx = async () => {
    setExporting(true);
    try { saveBlob(await qualityNcrAPI.exportInternal(), 'F-QA-13.xlsx'); } catch (e) { toast.error(await errMsg(e, 'Export failed')); } finally { setExporting(false); }
  };
  return (
    <div className="space-y-4" data-testid="internal-ncr-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Internal NC Register</h1>
          <p className="text-xs text-muted-foreground">Every MRB “reject” on production quarantine lands here automatically — fill the closure actions to resolve it.</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && <LegacyImportButton kind="internal" onDone={load} />}
          <Button variant="outline" size="sm" onClick={exportXlsx} disabled={exporting} data-testid="internal-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}Download F/QA/13</Button>
        </div>
      </div>
      <Card className="shadow-none"><CardContent className="p-3 flex flex-wrap items-center gap-3">
        <div className="flex rounded-md border overflow-hidden" data-testid="internal-status-tabs">
          {STATUSES.map((s) => (
            <button key={s || 'all'} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs ${status === s ? 'bg-teal-600 text-white' : 'hover:bg-muted'}`} data-testid={`internal-status-${s || 'all'}`}>{s || 'All'}</button>
          ))}
        </div>
        <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search NCR, part, job card, WO" className="pl-8 h-9 w-72" data-testid="internal-search-input" /></div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </CardContent></Card>
      <Card className="shadow-none"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs" data-testid="internal-table">
          <thead className="bg-muted/50 text-muted-foreground"><tr>
            {['NCR', 'Reported', 'Customer', 'Part', 'Job card', 'Qty', 'Reason', 'R/N', 'Closure', '8D', 'Status'].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={11} className="text-center py-6 text-muted-foreground">No internal rejections</td></tr>}
            {rows.map((n) => (
              <tr key={n.id} className="border-t hover:bg-muted/40 cursor-pointer" onClick={() => navigate(`/quality/internal-ncr/${n.id}`)} data-testid={`internal-row-${n.id}`}>
                <td className="px-3 py-2 font-mono text-blue-700">{n.number}</td>
                <td className="px-3 py-2">{fmtDate(n.reported_date)}</td>
                <td className="px-3 py-2">{n.customer_name}</td>
                <td className="px-3 py-2"><span className="font-mono">{n.part_no}</span> <span className="text-muted-foreground">{n.part_name}</span></td>
                <td className="px-3 py-2 font-mono">{n.job_card_number}</td>
                <td className="px-3 py-2 font-mono">{n.quantity}</td>
                <td className="px-3 py-2">{n.reason || (n.legacy ? <span className="text-muted-foreground">—</span> : <span className="text-orange-600">Not set</span>)}</td>
                <td className={`px-3 py-2 font-semibold ${n.repetitive === 'R' ? 'text-red-600' : ''}`}>{n.repetitive}</td>
                <td className="px-3 py-2">{n.closure_complete || (n.legacy && n.status === 'CLOSED') ? <Check className="w-4 h-4 text-emerald-600" /> : <span className="text-orange-600">Pending</span>}</td>
                <td className="px-3 py-2">{n.eight_d_required ? (n.eight_d_complete || n.has_8d_file ? <Check className="w-4 h-4 text-emerald-600" /> : <span className="text-orange-600">Required</span>) : '—'}</td>
                <td className="px-3 py-2"><StatusBadge status={n.status} testid={`internal-status-badge-${n.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
